import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Zap,
  ArrowLeft,
  Check,
  Sparkles,
  Gift,
  Globe,
  CreditCard,
  ChevronDown,
  ChevronUp,
  Users,
  Briefcase,
  ShoppingBag,
  ArrowRight,
  Star,
  Shield,
  TrendingUp,
  Target,
  Award,
  Rocket,
  Lock,
  MessageSquare,
  FileText,
  Eye,
  Search
} from "lucide-react";

// ─── Existing Tier Data (PRESERVED) ───
const RECRUITER_TIERS = [
  {
    id: "recruiter_starter", name: "Starter", price: 29, trialDays: 14,
    label: "Best for light monthly recruiting",
    features: ["3 active job postings", "10 AI job descriptions per month", "Basic candidate ranking", "Compare 2 candidates", "5 AI interviews per month", "Email notifications"],
    cta: "Choose Starter", highlighted: false
  },
  {
    id: "recruiter_pro", name: "Pro", price: 79, trialDays: 14,
    label: "Best for growing recruiting teams",
    features: ["10 active job postings", "Unlimited AI job descriptions", "20 AI interviews per month", "Compare 5 candidates", "Save job templates", "Shortlisted dashboard", "Bulk messaging"],
    cta: "Choose Pro", highlighted: true
  },
  {
    id: "recruiter_elite", name: "Elite", price: 149, trialDays: 14,
    label: "Best for high-volume recruiting",
    features: ["Unlimited job postings", "Unlimited AI interviews", "Priority matching", "Export reports (PDF)", "Compare unlimited candidates", "Historical talent pools", "Early feature access", "Dedicated support"],
    cta: "Choose Elite", highlighted: false
  }
];

const JOBSEEKER_TIERS = [
  {
    id: "jobseeker_free", name: "Free", price: 0,
    label: "Best for getting started",
    features: ["Profile creation", "Apply to jobs", "Basic role-fit score", "Standard visibility", "2 AI practice interviews", "Community access"],
    cta: "Create Free Profile", highlighted: false
  },
  {
    id: "jobseeker_plus", name: "Plus", price: 9,
    label: "Best for candidates who want a stronger edge",
    features: ["Enhanced visibility", "Career insights", "10 AI practice interviews", "Job recommendations", "Detailed role-fit analysis", "Email digests", "Interview reminders"],
    cta: "Choose Plus", highlighted: true
  },
  {
    id: "jobseeker_pro", name: "Pro", price: 19,
    label: "Best for candidates who want maximum preparation and visibility",
    features: ["Priority ranking", "Unlimited AI interviews", "AI interview feedback", "Resume optimization", "Skill gap analysis", "Early job access", "Market insights reports", "Profile analytics"],
    cta: "Choose Pro", highlighted: false
  }
];

const RECRUITER_FREE = {
  label: "Best for getting started",
  features: [
    "Recruiter account creation",
    "Company / recruiter profile setup",
    "View limited candidate previews",
    "Receive applicants from posted jobs",
    "Basic recruiter dashboard",
    "Limited access to AI hiring workflow",
    "1 active basic job posting",
    "1 free trial AI first-stage interview",
    "1 candidate comparison session",
    "Email notifications"
  ]
};

const RECRUITER_PAYG = {
  label: "Best for occasional hiring",
  items: [
    { name: "Promote a Job", desc: "Boost visibility with priority placement", prices: ["$29/7d", "$49/14d", "$79/30d"] },
    { name: "Unlock Candidate Details", desc: "Reveal contact info for candidates", prices: ["5 for $9", "15 for $19"] },
    { name: "Buy Outreach Credits", desc: "Directly message candidates", prices: ["10 for $19", "25 for $39"] },
    { name: "AI First-Stage Interview", desc: "Screen candidates with AI", prices: ["Included in credit packs"] },
    { name: "Extra Candidate Comparison", desc: "Compare more candidates side-by-side", prices: ["Via AI credits"] },
  ]
};

const JOBSEEKER_PAYG = {
  label: "Best for occasional extra support",
  items: [
    { name: "Boost My CV", desc: "AI-powered CV generation & optimization" },
    { name: "Practice Interviews", desc: "AI mock interview sessions" },
    { name: "Improve My Applications", desc: "AI cover letter generation" },
    { name: "Salary Negotiation Support", desc: "Get AI-powered negotiation advice" },
    { name: "Detailed Role-Fit Review", desc: "AI-enhanced job matching analysis" },
  ]
};

// Nigerian payment tier mapping (PRESERVED)
const NIGERIAN_TIERS = {
  recruiter: {
    recruiter_starter: { tier: "starter", price: 44000, name: "Starter" },
    recruiter_pro: { tier: "professional", price: 119000, name: "Pro" },
    recruiter_elite: { tier: "business", price: 224000, name: "Elite" }
  },
  jobseeker: {
    jobseeker_free: { tier: "basic", price: 0, name: "Free" },
    jobseeker_plus: { tier: "premium", price: 15000, name: "Plus" },
    jobseeker_pro: { tier: "elite", price: 30000, name: "Pro" }
  }
};

const FAQS = [
  { q: "Can I use Rekrutor without a subscription?", a: "Yes. Recruiters and job seekers can start free. Use pay-as-you-go for extras, or upgrade to a subscription when you need more." },
  { q: "What is pay-as-you-go?", a: "Pay-as-you-go lets you buy specific services only when needed — like promoting a job or unlocking candidate contacts — instead of committing to a monthly plan." },
  { q: "Do job seekers have to pay to apply?", a: "No. Applying for jobs stays free. You only pay if you want premium tools like CV optimization, extra interview practice, or enhanced visibility." },
  { q: "Who should use Recruiter Free?", a: "Recruiters who want to test Rekrutor before paying. Explore the platform, set up your profile, and see how it works before committing." },
  { q: "Who should use Recruiter Pay-as-you-go?", a: "Recruiters who hire occasionally and only need selected services like candidate unlocks, promoted jobs, or AI interviews." },
  { q: "Who should use Starter, Pro, or Elite?", a: "Recruiters who hire regularly and want bundled monthly value with higher limits and more features." },
  { q: "Can subscription users still use pay-as-you-go?", a: "Yes. If you exceed your plan limits before renewal, you can buy extras through pay-as-you-go anytime." },
  { q: "Does Rekrutor support local currencies?", a: "Yes. Rekrutor supports currency localization and user currency selection. Nigerian users can pay via Paystack or Flutterwave in NGN." },
];

const Subscription = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useAuth();
  const [selectedTier, setSelectedTier] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userCountry, setUserCountry] = useState(null);
  const [currencyPreference, setCurrencyPreference] = useState(null);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [selectedPaymentTier, setSelectedPaymentTier] = useState(null);
  const [activeTab, setActiveTab] = useState("recruiter");
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [openFaq, setOpenFaq] = useState(null);

  // ─── Preserved: currency preference fetch ───
  useEffect(() => {
    const fetchCurrencyPreference = async () => {
      if (!user) return;
      try {
        const response = await fetch(`${API}/users/currency-preference`, { credentials: "include" });
        if (response.ok) {
          const data = await response.json();
          setCurrencyPreference(data.currency_preference);
        }
      } catch {}
    };
    fetchCurrencyPreference();
  }, [user]);

  const getCountryFromLocale = () => {
    try {
      const locale = navigator.language || navigator.userLanguage;
      if (locale && locale.includes('-')) {
        const countryCode = locale.split('-')[1]?.toUpperCase();
        if (countryCode && countryCode.length === 2) return countryCode;
      }
    } catch {}
    return null;
  };

  // ─── Preserved: country detection ───
  useEffect(() => {
    const detectCountry = async () => {
      if (currencyPreference && currencyPreference !== "auto") return;
      const localeCountry = getCountryFromLocale();
      if (localeCountry) { setUserCountry(localeCountry); return; }
      try {
        const response = await fetch("https://ipapi.co/json/");
        if (response.ok) { const data = await response.json(); setUserCountry(data.country_code); }
      } catch { setUserCountry("US"); }
    };
    detectCountry();
  }, [currencyPreference]);

  // Set initial tab based on user role
  useEffect(() => {
    if (user?.role === "recruiter" || user?.role === "admin") setActiveTab("recruiter");
    else if (user?.role === "jobseeker") setActiveTab("jobseeker");
  }, [user]);

  const isNewRecruiter = location.state?.isNewRecruiter;
  const currentTier = user?.subscription_tier;
  const isNigerian = currencyPreference === "NGN" || (currencyPreference !== "USD" && userCountry === "NG");

  const getPrice = (basePrice) => {
    if (billingPeriod === "annual") return Math.round(basePrice * 12 * 0.83 / 12);
    return basePrice;
  };

  const getAnnualTotal = (basePrice) => Math.round(basePrice * 12 * 0.83);

  // ─── Preserved: handleStartTrial ───
  const handleStartTrial = async (tierId) => {
    if (!user) { toast.error("Please log in first"); return; }
    setLoading(true); setSelectedTier(tierId);
    try {
      const response = await fetch(`${API}/payments/subscribe`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ tier_id: tierId, is_trial: true }),
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`14-day free trial started!`);
        if (data.user) setUser(data.user);
        navigate("/dashboard/recruiter");
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || "Failed to start trial");
      }
    } catch { toast.error("Failed to start trial"); }
    finally { setLoading(false); setSelectedTier(null); }
  };

  // ─── Preserved: handleSubscribe ───
  const handleSubscribe = async (tierId) => {
    if (!user) {
      const redirectUrl = window.location.origin + "/auth/callback";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    if (isNigerian && tierId !== "jobseeker_free") {
      setSelectedPaymentTier(tierId); setShowPaymentOptions(true); return;
    }
    await processStripePayment(tierId);
  };

  // ─── Preserved: processStripePayment ───
  const processStripePayment = async (tierId) => {
    setLoading(true); setSelectedTier(tierId);
    try {
      const response = await fetch(`${API}/payments/create-checkout`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ tier_id: tierId, origin_url: window.location.origin }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.url) { window.location.href = data.url; }
        else { toast.success("Plan activated!"); navigate(user.role === "recruiter" ? "/dashboard/recruiter" : "/dashboard/jobseeker"); }
      } else { toast.error("Failed to start checkout"); }
    } catch { toast.error("Failed to start checkout"); }
    finally { setLoading(false); setSelectedTier(null); }
  };

  // ─── Preserved: handleNigerianPayment ───
  const handleNigerianPayment = async (gateway) => {
    if (!selectedPaymentTier || !user) return;
    setLoading(true); setSelectedTier(selectedPaymentTier); setShowPaymentOptions(false);
    const role = user.role || "jobseeker";
    const tierMapping = NIGERIAN_TIERS[role]?.[selectedPaymentTier];
    if (!tierMapping) { toast.error("Invalid tier selected"); setLoading(false); return; }
    try {
      const response = await fetch(`${API}/${gateway}/initialize`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ tier: tierMapping.tier, billing_period: "monthly" }),
      });
      if (response.ok) {
        const data = await response.json();
        const redirectUrl = gateway === "paystack" ? data.authorization_url : data.payment_link;
        if (redirectUrl) { window.location.href = redirectUrl; }
        else { toast.error("Failed to initialize payment"); }
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || "Failed to initialize payment");
      }
    } catch { toast.error("Failed to connect to payment service"); }
    finally { setLoading(false); setSelectedTier(null); setSelectedPaymentTier(null); }
  };

  const handleSkipForNow = () => navigate("/dashboard/recruiter");

  const handleGetStartedFree = () => {
    if (user) {
      navigate(user.role === "recruiter" ? "/dashboard/recruiter" : "/dashboard/jobseeker");
    } else {
      const redirectUrl = window.location.origin + "/auth/callback";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };

  const handleBuyCredits = () => {
    if (user) navigate("/addons");
    else {
      const redirectUrl = window.location.origin + "/auth/callback";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };

  // ─── Subscription Tier Card ───
  const TierCard = ({ tier, index, isRecruiter }) => {
    const isCurrent = currentTier === tier.id;
    const displayPrice = getPrice(tier.price);
    const isAnnual = billingPeriod === "annual";

    return (
      <motion.div
        className={`relative p-5 sm:p-6 border transition-all flex flex-col ${
          tier.highlighted ? "border-crimson bg-crimson/5" : "border-[#222] bg-[#0a0a0a] hover:border-[#333]"
        } ${isCurrent ? "ring-2 ring-crimson/60" : ""}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 + index * 0.08 }}
        data-testid={`tier-${tier.id}`}
      >
        {tier.highlighted && (
          <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-4 py-0.5 bg-crimson text-[10px] uppercase tracking-widest whitespace-nowrap">Most Popular</div>
        )}
        {isCurrent && (
          <div className="absolute -top-2.5 right-3 px-3 py-0.5 bg-emerald-500 text-[10px] uppercase tracking-widest text-black">Current</div>
        )}
        <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">{tier.label}</p>
        <h3 className="font-heading text-xl font-bold mb-1">{tier.name}</h3>
        <div className="flex items-baseline gap-1 mb-1">
          <span className="font-heading text-3xl font-bold text-crimson">${displayPrice}</span>
          <span className="text-gray-500 text-sm">/mo</span>
        </div>
        {isAnnual && tier.price > 0 && (
          <p className="text-emerald-400 text-xs mb-3">${getAnnualTotal(tier.price)}/yr — Save 17%</p>
        )}
        {!isAnnual && tier.price > 0 && <div className="mb-3" />}
        {isRecruiter && isNewRecruiter && tier.trialDays && (
          <p className="text-emerald-400 text-xs mb-3">14-day free trial included</p>
        )}
        <ul className="space-y-2.5 mb-6 flex-1">
          {tier.features.map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
              <Check className="w-3.5 h-3.5 text-crimson flex-shrink-0 mt-0.5" />{f}
            </li>
          ))}
        </ul>
        {isNewRecruiter && isRecruiter ? (
          <Button onClick={() => handleStartTrial(tier.id)} disabled={loading}
            className={`w-full ${tier.highlighted ? "bg-crimson hover:bg-crimson/90 text-white" : "bg-transparent border border-[#333] hover:border-crimson text-white"}`}
            data-testid={`trial-${tier.id}`}>
            {loading && selectedTier === tier.id ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Start Free Trial"}
          </Button>
        ) : (
          <Button onClick={() => tier.price === 0 ? handleGetStartedFree() : handleSubscribe(tier.id)}
            disabled={loading || isCurrent}
            className={`w-full ${tier.highlighted ? "bg-crimson hover:bg-crimson/90 text-white" : "bg-transparent border border-[#333] hover:border-crimson text-white"} ${isCurrent ? "opacity-50" : ""}`}
            data-testid={`subscribe-${tier.id}`}>
            {loading && selectedTier === tier.id ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : isCurrent ? "Current Plan" : tier.cta}
          </Button>
        )}
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-obsidian" data-testid="pricing-page">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-obsidian/80 backdrop-blur-md border-b border-[#222]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {!isNewRecruiter && (
              <Link to={user?.role === "recruiter" ? "/dashboard/recruiter" : user?.role === "jobseeker" ? "/dashboard/jobseeker" : "/"} className="text-gray-400 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
            )}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-crimson flex items-center justify-center"><Zap className="w-5 h-5 text-white" /></div>
              <span className="font-heading text-lg font-bold">PRICING</span>
            </div>
          </div>
          {isNewRecruiter && (
            <button onClick={handleSkipForNow} className="text-gray-400 hover:text-white text-sm transition-colors" data-testid="skip-subscription-btn">
              Skip for now <ArrowRight className="w-3 h-3 inline ml-1" />
            </button>
          )}
        </div>
      </header>

      <main className="pt-20 pb-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">

          {/* ── Hero ── */}
          <motion.div className="text-center mb-10" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
            {isNewRecruiter && (
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-500/10 border border-emerald-500/30 mb-5">
                <Gift className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-emerald-400">Start with a 14-day free trial — No credit card required</span>
              </div>
            )}
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-3">
              HIRING PRICING THAT <span className="text-crimson">MAKES SENSE</span>
            </h1>
            <p className="text-gray-400 max-w-2xl mx-auto text-sm sm:text-base">
              Start free. Pay only for the reach, access, and hiring tools you actually need. Upgrade when hiring becomes more frequent.
            </p>
            {currentTier && !isNewRecruiter && (
              <p className="mt-3 text-xs text-gray-500">
                Current plan: <span className="text-crimson uppercase font-medium">{currentTier.replace(/_/g, " ")}</span>
              </p>
            )}
            <div className="flex items-center justify-center gap-3 mt-6">
              <Button onClick={handleGetStartedFree} className="bg-crimson hover:bg-crimson/90 text-white px-6" data-testid="hero-get-started">
                Get Started Free
              </Button>
              <Button onClick={() => document.getElementById("pricing-tabs")?.scrollIntoView({ behavior: "smooth" })}
                variant="outline" className="border-[#333] text-white hover:border-crimson px-6" data-testid="hero-start-recruiting">
                Start Recruiting
              </Button>
            </div>
          </motion.div>

          {/* ── Pricing Philosophy Strip ── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            {[
              { icon: Users, title: "Job seekers join free", desc: "Create your profile, upload your CV, and apply for jobs without paying to get started." },
              { icon: Briefcase, title: "Recruiters start free", desc: "Explore Rekrutor, test the workflow, and only pay when you need more visibility, access, or screening power." },
              { icon: TrendingUp, title: "Subscriptions are for frequent users", desc: "If you use Rekrutor regularly, bundled plans give you better value and more control." },
            ].map((item, i) => (
              <motion.div key={i} className="p-5 bg-[#0a0a0a] border border-[#1a1a1a]" initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.08 }}>
                <item.icon className="w-5 h-5 text-crimson mb-3" />
                <h3 className="font-heading text-sm font-bold mb-1">{item.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* ── Tab Structure ── */}
          <div id="pricing-tabs" className="scroll-mt-24">
            <div className="flex items-center justify-center gap-2 mb-6">
              {["recruiter", "jobseeker"].map((tab) => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className={`px-5 py-2.5 text-sm font-medium transition-colors ${
                    activeTab === tab ? "bg-crimson text-white" : "bg-[#111] border border-[#222] text-gray-400 hover:text-white"
                  }`}
                  data-testid={`tab-${tab}`}
                >
                  {tab === "recruiter" ? "Recruiters" : "Job Seekers"}
                </button>
              ))}
            </div>

            {/* Monthly / Annual Toggle */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <span className={`text-sm ${billingPeriod === "monthly" ? "text-white" : "text-gray-500"}`}>Monthly</span>
              <button onClick={() => setBillingPeriod(billingPeriod === "monthly" ? "annual" : "monthly")}
                className="relative w-12 h-6 bg-[#222] rounded-full transition-colors" data-testid="billing-toggle">
                <div className={`absolute top-0.5 w-5 h-5 bg-crimson rounded-full transition-transform ${billingPeriod === "annual" ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
              <span className={`text-sm ${billingPeriod === "annual" ? "text-white" : "text-gray-500"}`}>
                Annual <span className="text-emerald-400 text-xs ml-1">Save 17%</span>
              </span>
            </div>
          </div>

          {/* ════════════════ RECRUITER TAB ════════════════ */}
          {activeTab === "recruiter" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key="recruiter">
              {/* Row 1: Free + Pay-as-you-go */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Recruiter Free */}
                <motion.div className="p-5 sm:p-6 border border-[#222] bg-[#0a0a0a]" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="tier-recruiter-free">
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">{RECRUITER_FREE.label}</p>
                  <h3 className="font-heading text-xl font-bold mb-1">Recruiter Free</h3>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="font-heading text-3xl font-bold text-crimson">$0</span>
                    <span className="text-gray-500 text-sm">/mo</span>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {RECRUITER_FREE.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                        <Check className="w-3.5 h-3.5 text-crimson flex-shrink-0 mt-0.5" />{f}
                      </li>
                    ))}
                  </ul>
                  <Button onClick={handleGetStartedFree} className="w-full bg-transparent border border-[#333] hover:border-crimson text-white" data-testid="cta-recruiter-free">
                    Start Free
                  </Button>
                  <p className="text-[10px] text-gray-600 mt-3 text-center">Best for recruiters testing the platform before paying for extra access or moving to a full plan.</p>
                </motion.div>

                {/* Recruiter Pay-as-you-go */}
                <motion.div className="p-5 sm:p-6 border border-[#222] bg-[#0a0a0a]" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} data-testid="tier-recruiter-payg">
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">{RECRUITER_PAYG.label}</p>
                  <h3 className="font-heading text-xl font-bold mb-1">Pay-as-you-go</h3>
                  <p className="text-sm text-gray-400 mb-4">Pay only for what you need, when you need it.</p>
                  <div className="space-y-3 mb-6">
                    {RECRUITER_PAYG.items.map((item, i) => (
                      <div key={i} className="flex items-start justify-between gap-2 p-2.5 bg-[#111] border border-[#1a1a1a]">
                        <div>
                          <p className="text-sm font-medium text-white">{item.name}</p>
                          <p className="text-[10px] text-gray-500">{item.desc}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          {item.prices?.map((p, j) => <p key={j} className="text-[10px] text-crimson">{p}</p>)}
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button onClick={handleBuyCredits} className="w-full bg-crimson hover:bg-crimson/90 text-white" data-testid="cta-recruiter-payg">
                    Buy Credits
                  </Button>
                  <p className="text-[10px] text-gray-600 mt-3 text-center">Best for recruiters who do not hire often enough to justify a monthly plan.</p>
                </motion.div>
              </div>

              {/* Row 2: Starter / Pro / Elite */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {RECRUITER_TIERS.map((tier, i) => (
                  <TierCard key={tier.id} tier={tier} index={i} isRecruiter={true} />
                ))}
              </div>

              {/* Recruiter Support Text */}
              <p className="text-center text-xs text-gray-500 mb-10">
                Not ready for a monthly plan? Start free or use pay-as-you-go for featured jobs, candidate unlocks, outreach credits, and extra AI interviews.
              </p>
            </motion.div>
          )}

          {/* ════════════════ JOB SEEKER TAB ════════════════ */}
          {activeTab === "jobseeker" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key="jobseeker">
              {/* Row 1: Free + Pay-as-you-go */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Job Seeker Free */}
                <TierCard tier={JOBSEEKER_TIERS[0]} index={0} isRecruiter={false} />

                {/* Job Seeker Pay-as-you-go */}
                <motion.div className="p-5 sm:p-6 border border-[#222] bg-[#0a0a0a] flex flex-col" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} data-testid="tier-jobseeker-payg">
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">{JOBSEEKER_PAYG.label}</p>
                  <h3 className="font-heading text-xl font-bold mb-1">Pay-as-you-go</h3>
                  <p className="text-sm text-gray-400 mb-4">Use premium tools only when you need them.</p>
                  <div className="space-y-2.5 mb-6 flex-1">
                    {JOBSEEKER_PAYG.items.map((item, i) => (
                      <div key={i} className="flex items-start gap-2 p-2.5 bg-[#111] border border-[#1a1a1a]">
                        <Sparkles className="w-3.5 h-3.5 text-crimson flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-white">{item.name}</p>
                          <p className="text-[10px] text-gray-500">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button onClick={handleBuyCredits} className="w-full bg-crimson hover:bg-crimson/90 text-white" data-testid="cta-jobseeker-payg">
                    Use AI Tools
                  </Button>
                  <p className="text-[10px] text-gray-600 mt-3 text-center">Best for job seekers who want occasional help without subscribing.</p>
                </motion.div>
              </div>

              {/* Row 2: Plus / Pro */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {JOBSEEKER_TIERS.slice(1).map((tier, i) => (
                  <TierCard key={tier.id} tier={tier} index={i} isRecruiter={false} />
                ))}
              </div>

              {/* Job Seeker Support Text */}
              <p className="text-center text-xs text-gray-500 mb-10">
                Applying for jobs stays free. Use pay-as-you-go tools for one-off help, or upgrade for ongoing support and better visibility.
              </p>
            </motion.div>
          )}

          {/* ── How Rekrutor Pricing Works ── */}
          <div className="border-t border-[#1a1a1a] pt-12 mb-12">
            <motion.div className="text-center mb-8" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-2">HOW REKRUTOR PRICING <span className="text-crimson">WORKS</span></h2>
              <p className="text-gray-400 text-sm">Start free. Pay as needed. Upgrade when usage becomes consistent.</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <motion.div className="p-6 bg-[#0a0a0a] border border-[#1a1a1a]" initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                <Briefcase className="w-5 h-5 text-crimson mb-3" />
                <h3 className="font-heading text-lg font-bold mb-3">For Recruiters</h3>
                <div className="space-y-3 text-sm text-gray-400">
                  <p>Start with a <span className="text-white">free account</span>.</p>
                  <p>If you only need occasional help, use <span className="text-white">pay-as-you-go</span> services like candidate unlocks, promoted jobs, or extra AI interviews.</p>
                  <p>If you hire regularly, move to <span className="text-crimson">Starter, Pro, or Elite</span> for better value and included monthly usage.</p>
                </div>
              </motion.div>
              <motion.div className="p-6 bg-[#0a0a0a] border border-[#1a1a1a]" initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                <Users className="w-5 h-5 text-crimson mb-3" />
                <h3 className="font-heading text-lg font-bold mb-3">For Job Seekers</h3>
                <div className="space-y-3 text-sm text-gray-400">
                  <p>Create your account and <span className="text-white">apply for jobs for free</span>.</p>
                  <p>If you want occasional support, use <span className="text-white">pay-as-you-go</span> tools like CV boosting or extra interview practice.</p>
                  <p>If you use Rekrutor often, subscribe to <span className="text-crimson">Plus or Pro</span> for more visibility and ongoing support.</p>
                </div>
              </motion.div>
            </div>
          </div>

          {/* ── Comparison Table ── */}
          <div className="border-t border-[#1a1a1a] pt-12 mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-center mb-8">FIND YOUR <span className="text-crimson">FIT</span></h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {/* Recruiter comparison */}
              <div className="space-y-2">
                <h3 className="font-heading text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Recruiters</h3>
                {[
                  { tier: "Recruiter Free", use: "Testing Rekrutor", color: "text-gray-400" },
                  { tier: "Pay-as-you-go", use: "Occasional hiring", color: "text-amber-400" },
                  { tier: "Starter", use: "Light monthly recruiting", color: "text-blue-400" },
                  { tier: "Pro", use: "Regular team recruiting", color: "text-crimson" },
                  { tier: "Elite", use: "High-volume recruiting", color: "text-emerald-400" },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-[#0a0a0a] border border-[#1a1a1a]">
                    <span className={`text-sm font-medium ${row.color}`}>{row.tier}</span>
                    <span className="text-xs text-gray-500">{row.use}</span>
                  </div>
                ))}
              </div>
              {/* Job Seeker comparison */}
              <div className="space-y-2">
                <h3 className="font-heading text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Job Seekers</h3>
                {[
                  { tier: "Free", use: "Starting out", color: "text-gray-400" },
                  { tier: "Pay-as-you-go", use: "One-off support", color: "text-amber-400" },
                  { tier: "Plus", use: "Active job seekers", color: "text-crimson" },
                  { tier: "Pro", use: "Serious job seekers", color: "text-emerald-400" },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-[#0a0a0a] border border-[#1a1a1a]">
                    <span className={`text-sm font-medium ${row.color}`}>{row.tier}</span>
                    <span className="text-xs text-gray-500">{row.use}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── FAQ ── */}
          <div className="border-t border-[#1a1a1a] pt-12 mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-center mb-8">FREQUENTLY ASKED <span className="text-crimson">QUESTIONS</span></h2>
            <div className="max-w-3xl mx-auto space-y-2">
              {FAQS.map((faq, i) => (
                <div key={i} className="border border-[#1a1a1a] bg-[#0a0a0a]">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-4 text-left" data-testid={`faq-${i}`}>
                    <span className="text-sm font-medium pr-4">{faq.q}</span>
                    {openFaq === i ? <ChevronUp className="w-4 h-4 text-crimson flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />}
                  </button>
                  <AnimatePresence>
                    {openFaq === i && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                        <p className="px-4 pb-4 text-sm text-gray-400">{faq.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>

          {/* ── Final CTA ── */}
          <motion.div className="text-center py-12 border-t border-[#1a1a1a]" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-3">START FREE. <span className="text-crimson">UPGRADE WHEN THE NEED IS REAL.</span></h2>
            <p className="text-gray-400 text-sm mb-6 max-w-lg mx-auto">No commitment required. Explore Rekrutor at your own pace.</p>
            <div className="flex items-center justify-center gap-3">
              <Button onClick={handleGetStartedFree} className="bg-crimson hover:bg-crimson/90 text-white px-8" data-testid="final-get-started">
                Get Started Free
              </Button>
              <Button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} variant="outline" className="border-[#333] text-white hover:border-crimson px-8" data-testid="final-explore-plans">
                Explore Plans
              </Button>
            </div>
            {isNigerian && (
              <p className="mt-4 text-gray-500 text-xs flex items-center justify-center gap-2">
                <Globe className="w-3 h-3" /> Nigerian payments powered by Paystack & Flutterwave
              </p>
            )}
          </motion.div>
        </div>
      </main>

      {/* ── Nigerian Payment Gateway Modal (PRESERVED) ── */}
      {showPaymentOptions && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div className="bg-obsidian-light border border-[#333] max-w-md w-full p-8" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <h3 className="font-heading text-2xl font-bold mb-2">Choose Payment Method</h3>
            <p className="text-gray-400 text-sm mb-6">Select your preferred Nigerian payment gateway</p>
            {selectedPaymentTier && (
              <div className="bg-obsidian p-4 border border-[#222] mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">{NIGERIAN_TIERS[user?.role || "jobseeker"]?.[selectedPaymentTier]?.name} Plan</span>
                  <span className="text-crimson font-bold">NGN {NIGERIAN_TIERS[user?.role || "jobseeker"]?.[selectedPaymentTier]?.price?.toLocaleString()}/mo</span>
                </div>
              </div>
            )}
            <div className="space-y-3">
              <Button onClick={() => handleNigerianPayment("paystack")} disabled={loading}
                className="w-full bg-[#00C3F7] hover:bg-[#00A8D6] text-white py-6 flex items-center justify-center gap-3" data-testid="pay-with-paystack">
                {loading && selectedTier ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><CreditCard className="w-5 h-5" /> Pay with Paystack</>}
              </Button>
              <Button onClick={() => handleNigerianPayment("flutterwave")} disabled={loading}
                className="w-full bg-[#F5A623] hover:bg-[#E09612] text-black py-6 flex items-center justify-center gap-3" data-testid="pay-with-flutterwave">
                {loading && selectedTier ? <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <><CreditCard className="w-5 h-5" /> Pay with Flutterwave</>}
              </Button>
            </div>
            <button onClick={() => { setShowPaymentOptions(false); setSelectedPaymentTier(null); }} className="w-full mt-4 text-gray-400 hover:text-white text-sm py-2">Cancel</button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Subscription;
